// =============================================================================
// PEDRA — src/lib/pdfReport.ts
// Relatorio do plano de corte em PDF, pronto para baixar.
//
//   pagina 1   capa: numeros principais, resumo, configuracoes, lista das
//              chapas, pecas que nao couberam, fitas e avisos;
//   depois     uma secao por chapa: o diagrama (vetorial, igual ao da tela),
//              as pecas daquela chapa, a sequencia de cortes e as sobras.
//
// Cada chapa escolhe a orientacao da pagina (retrato ou paisagem) que deixa o
// desenho maior. As tabelas quebram de pagina repetindo o cabecalho.
//
// Este modulo nao conhece React: quem chama entrega `renderDiagram`, que
// devolve o <svg> de uma chapa (ver PdfDownloadButton).
// =============================================================================

import type { EdgeBandType, OptimizationResult, Settings, SheetLayout } from '../core/types';
import {
  formatArea,
  formatBigLength,
  formatDimensions,
  formatKerf,
  formatLength,
  formatPercent,
} from '../core/units';
import { downloadBlob } from './csv';
import { formatCount, formatDateTime, formatFileStamp, priorityLabel, slugifyFilename, yesNo } from './format';
import type { PdfColor, PdfFont, PdfPage } from './pdf';
import { A4_LANDSCAPE, A4_PORTRAIT, PdfDocument, fitTextWidth, measureText, parseColor } from './pdf';
import { drawSvg, svgAspect } from './pdfSvg';

// -----------------------------------------------------------------------------
// ENTRADA
// -----------------------------------------------------------------------------

export interface PdfReportData {
  projectName: string;
  result: OptimizationResult;
  settings: Settings;
  edgeBands: readonly EdgeBandType[];
  /** true quando as entradas mudaram depois do calculo: o PDF avisa. */
  stale?: boolean;
  /**
   * Devolve o <svg> da chapa, ja desenhado. Chamado uma chapa por vez, e o
   * elemento so precisa existir ate a chamada seguinte. `null` = sem desenho.
   */
  renderDiagram?(sheet: SheetLayout): Element | null;
  /** Data impressa no cabecalho (padrao: agora). */
  now?: Date;
}

/** Nome do arquivo: `cozinha-plano-de-corte-2026-09-28.pdf`. */
export function pdfFilename(projectName: string, now: Date = new Date()): string {
  return `${slugifyFilename(projectName)}-plano-de-corte-${formatFileStamp(now)}.pdf`;
}

// -----------------------------------------------------------------------------
// ESTILO DO PAPEL
// -----------------------------------------------------------------------------

function rgb(hex: string): PdfColor {
  return parseColor(hex) ?? [0, 0, 0];
}

/** Espelho do tema claro de theme.css (papel e sempre claro). */
const C = {
  text: rgb('#16202b'),
  muted: rgb('#55616e'),
  rule: rgb('#b9c3ce'),
  line: rgb('#dde3ea'),
  headBg: rgb('#edf1f5'),
  zebra: rgb('#f5f7fa'),
  accent: rgb('#17654a'),
  accentSoft: rgb('#e3f1ea'),
  danger: rgb('#c02626'),
  dangerBg: rgb('#fdecec'),
  warning: rgb('#9a5b00'),
  warningBg: rgb('#fff4e0'),
} as const;

const MARGIN = 36;
/** Faixa do rodape, abaixo da area util. */
const FOOTER_H = 22;

const ROW_H = 14;
/** Entrelinha das celulas que quebram linha. */
const CELL_LINE_H = 10.5;
/** Teto de linhas por celula: dali em diante o texto ganha reticencias. */
const CELL_MAX_LINES = 4;
const TABLE_FONT = 8.5;
const HEAD_FONT = 8;
const CELL_PAD = 4;

// -----------------------------------------------------------------------------
// FLUXO DE PAGINAS
// -----------------------------------------------------------------------------

type Orientation = 'portrait' | 'landscape';

class Flow {
  readonly doc: PdfDocument;
  page: PdfPage;
  y = MARGIN;
  orientation: Orientation;

  constructor(doc: PdfDocument, orientation: Orientation) {
    this.doc = doc;
    this.orientation = orientation;
    this.page = this.addPage(orientation);
  }

  private addPage(orientation: Orientation): PdfPage {
    const size = orientation === 'landscape' ? A4_LANDSCAPE : A4_PORTRAIT;
    return this.doc.addPage(size.width, size.height);
  }

  get left(): number {
    return MARGIN;
  }

  get width(): number {
    return this.page.width - MARGIN * 2;
  }

  get bottom(): number {
    return this.page.height - MARGIN - FOOTER_H + 12;
  }

  get remaining(): number {
    return this.bottom - this.y;
  }

  newPage(orientation: Orientation = this.orientation): void {
    this.orientation = orientation;
    this.page = this.addPage(orientation);
    this.y = MARGIN;
  }

  /** Garante `h` pontos livres; senao abre pagina nova. Devolve true se abriu. */
  ensure(h: number): boolean {
    if (this.y + h <= this.bottom) return false;
    this.newPage();
    return true;
  }

  gap(h: number): void {
    this.y += h;
  }
}

// -----------------------------------------------------------------------------
// BLOCOS BASICOS
// -----------------------------------------------------------------------------

function sectionTitle(flow: Flow, text: string, tone: 'default' | 'danger' = 'default'): void {
  // Titulo nunca fica sozinho no pe da pagina: pede espaco para duas linhas.
  flow.ensure(20 + ROW_H * 2);
  const color = tone === 'danger' ? C.danger : C.text;
  flow.page.text(text, flow.left, flow.y + 11, { font: 'sansBold', size: 11, color });
  flow.page
    .strokeColor(tone === 'danger' ? C.danger : C.rule)
    .lineWidth(0.6)
    .dash([])
    .moveTo(flow.left, flow.y + 16)
    .lineTo(flow.left + flow.width, flow.y + 16)
    .stroke();
  flow.gap(22);
}

/** Pares "rotulo ... valor" em duas colunas. */
function keyValues(flow: Flow, rows: ReadonlyArray<readonly [string, string]>): void {
  const colGap = 18;
  const colW = (flow.width - colGap) / 2;
  const lineH = 15;
  for (let i = 0; i < rows.length; i += 2) {
    flow.ensure(lineH);
    for (let c = 0; c < 2; c++) {
      const row = rows[i + c];
      if (!row) continue;
      const [label, value] = row;
      const x = flow.left + c * (colW + colGap);
      const valueW = Math.min(colW * 0.6, measureText(value, 'sansBold', 9));
      const labelFit = fitTextWidth(label, colW - valueW - 8, 'sans', 9);
      flow.page.text(labelFit, x, flow.y + 10, { size: 9, color: C.muted });
      flow.page.text(fitTextWidth(value, colW * 0.6, 'sansBold', 9), x + colW, flow.y + 10, {
        font: 'sansBold',
        size: 9,
        align: 'right',
        color: C.text,
      });
      flow.page
        .strokeColor(C.line)
        .lineWidth(0.5)
        .dash([1, 1.5])
        .moveTo(x, flow.y + 13.5)
        .lineTo(x + colW, flow.y + 13.5)
        .stroke()
        .dash([]);
    }
    flow.gap(lineH);
  }
  flow.gap(8);
}

/** Paragrafo com quebra de linha pela largura real das letras. */
function wrapText(text: string, maxWidth: number, font: PdfFont, size: number): string[] {
  const lines: string[] = [];
  for (const para of text.split(/\r?\n/)) {
    const words = para.split(/\s+/).filter(Boolean);
    let line = '';
    for (const word of words) {
      const candidate = line === '' ? word : `${line} ${word}`;
      if (measureText(candidate, font, size) <= maxWidth || line === '') {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines.map((l) => fitTextWidth(l, maxWidth, font, size));
}

/** Caixa de aviso colorida (desatualizado, pecas em falta...). */
function notice(flow: Flow, text: string, tone: 'warning' | 'danger'): void {
  const size = 9;
  const pad = 7;
  // Quebra pela largura da NEGRITA: a primeira linha sai em negrito e a
  // Helvetica-Bold e mais larga — medida pela regular, ela passaria da caixa.
  const lines = wrapText(text, flow.width - pad * 2 - 6, 'sansBold', size);
  const h = lines.length * 12 + pad * 2 - 2;
  flow.ensure(h);
  const bg = tone === 'danger' ? C.dangerBg : C.warningBg;
  const fg = tone === 'danger' ? C.danger : C.warning;
  flow.page.fillColor(bg).roundedRect(flow.left, flow.y, flow.width, h, 3).fill();
  flow.page.fillColor(fg).rect(flow.left, flow.y, 3, h).fill();
  lines.forEach((line, i) => {
    flow.page.text(line, flow.left + pad + 4, flow.y + pad + 8 + i * 12, {
      font: i === 0 ? 'sansBold' : 'sans',
      size,
      color: fg,
    });
  });
  flow.gap(h + 10);
}

// -----------------------------------------------------------------------------
// TABELA
// -----------------------------------------------------------------------------

interface Column {
  header: string;
  /** Peso relativo da largura. */
  weight: number;
  align?: 'left' | 'right' | 'center';
  /** Quebra o texto em ate `CELL_MAX_LINES` linhas em vez de cortar. */
  wrap?: boolean;
}

/** Linhas de uma celula, ja cabendo na largura. */
function cellLines(text: string, col: Column, maxWidth: number): string[] {
  if (!col.wrap) return [fitTextWidth(text, maxWidth, 'sans', TABLE_FONT)];
  const lines = wrapText(text, maxWidth, 'sans', TABLE_FONT);
  if (lines.length <= CELL_MAX_LINES) return lines;
  const head = lines.slice(0, CELL_MAX_LINES - 1);
  const tail = fitTextWidth(`${lines.slice(CELL_MAX_LINES - 1).join(' ')}…`, maxWidth, 'sans', TABLE_FONT);
  return [...head, tail];
}

function drawTable(
  flow: Flow,
  columns: readonly Column[],
  rows: ReadonlyArray<ReadonlyArray<string>>,
  emptyMessage: string,
): void {
  if (rows.length === 0) {
    flow.ensure(ROW_H);
    flow.page.text(emptyMessage, flow.left, flow.y + 10, { size: 9, color: C.muted });
    flow.gap(ROW_H + 8);
    return;
  }

  const layout = (): { x: number; w: number }[] => {
    const total = columns.reduce((a, c) => a + c.weight, 0);
    let x = flow.left;
    return columns.map((c) => {
      const w = (flow.width * c.weight) / total;
      const out = { x, w };
      x += w;
      return out;
    });
  };

  const cellX = (col: Column, box: { x: number; w: number }): number =>
    col.align === 'right' ? box.x + box.w - CELL_PAD : col.align === 'center' ? box.x + box.w / 2 : box.x + CELL_PAD;

  const header = (): void => {
    const boxes = layout();
    flow.page.fillColor(C.headBg).rect(flow.left, flow.y, flow.width, ROW_H + 1).fill();
    columns.forEach((col, i) => {
      const box = boxes[i] as { x: number; w: number };
      const text = fitTextWidth(col.header, box.w - CELL_PAD * 2, 'sansBold', HEAD_FONT);
      flow.page.text(text, cellX(col, box), flow.y + ROW_H / 2 + 0.5, {
        font: 'sansBold',
        size: HEAD_FONT,
        align: col.align ?? 'left',
        baseline: 'middle',
        color: C.text,
      });
    });
    flow.page
      .strokeColor(C.rule)
      .lineWidth(0.6)
      .dash([])
      .moveTo(flow.left, flow.y + ROW_H + 1)
      .lineTo(flow.left + flow.width, flow.y + ROW_H + 1)
      .stroke();
    flow.gap(ROW_H + 1);
  };

  // Cabecalho + pelo menos uma linha, senao o cabecalho fica orfao.
  flow.ensure(ROW_H * 2 + 1);
  header();

  rows.forEach((row, r) => {
    const boxes = layout();
    const cells = columns.map((col, i) =>
      cellLines(row[i] ?? '', col, (boxes[i] as { w: number }).w - CELL_PAD * 2),
    );
    const lineCount = Math.max(1, ...cells.map((c) => c.length));
    const rowH = ROW_H + (lineCount - 1) * CELL_LINE_H;
    if (flow.ensure(rowH)) header();
    if (r % 2 === 1) flow.page.fillColor(C.zebra).rect(flow.left, flow.y, flow.width, rowH).fill();
    columns.forEach((col, i) => {
      const box = boxes[i] as { x: number; w: number };
      (cells[i] ?? []).forEach((line, l) => {
        flow.page.text(line, cellX(col, box), flow.y + ROW_H / 2 + 0.5 + l * CELL_LINE_H, {
          size: TABLE_FONT,
          align: col.align ?? 'left',
          baseline: 'middle',
          color: C.text,
        });
      });
    });
    flow.page
      .strokeColor(C.line)
      .lineWidth(0.4)
      .moveTo(flow.left, flow.y + rowH)
      .lineTo(flow.left + flow.width, flow.y + rowH)
      .stroke();
    flow.gap(rowH);
  });
  flow.gap(10);
}

// -----------------------------------------------------------------------------
// FORMATACAO
// -----------------------------------------------------------------------------

function dims(length: number, width: number, s: Settings): string {
  return formatDimensions(length, width, s.unit, s.decimalPlaces, s.dimensionOrder);
}

// -----------------------------------------------------------------------------
// CAPA
// -----------------------------------------------------------------------------

function kpis(flow: Flow, items: ReadonlyArray<readonly [string, string]>): void {
  const gap = 8;
  const h = 46;
  const w = (flow.width - gap * (items.length - 1)) / items.length;
  flow.ensure(h);
  items.forEach(([label, value], i) => {
    const x = flow.left + i * (w + gap);
    flow.page
      .fillColor(i === 0 ? C.accentSoft : C.zebra)
      .strokeColor(i === 0 ? C.accent : C.line)
      .lineWidth(0.6)
      .dash([])
      .roundedRect(x, flow.y, w, h, 4)
      .fillAndStroke();
    flow.page.text(fitTextWidth(value, w - 14, 'sansBold', 15), x + 8, flow.y + 22, {
      font: 'sansBold',
      size: 15,
      color: i === 0 ? C.accent : C.text,
    });
    flow.page.text(fitTextWidth(label, w - 14, 'sans', 8), x + 8, flow.y + 36, { size: 8, color: C.muted });
  });
  flow.gap(h + 16);
}

function buildCover(flow: Flow, data: PdfReportData, now: Date): void {
  const { result, settings } = data;
  const stats = result.stats;
  const page = flow.page;

  const title = data.projectName.trim() !== '' ? data.projectName.trim() : 'Plano de corte';
  page.text(fitTextWidth(title, flow.width, 'sansBold', 20), flow.left, flow.y + 18, {
    font: 'sansBold',
    size: 20,
    color: C.text,
  });
  page.text(`Plano de corte · gerado em ${formatDateTime(now)}`, flow.left, flow.y + 34, {
    size: 9.5,
    color: C.muted,
  });
  page
    .strokeColor(C.accent)
    .lineWidth(1.4)
    .dash([])
    .moveTo(flow.left, flow.y + 44)
    .lineTo(flow.left + flow.width, flow.y + 44)
    .stroke();
  flow.gap(58);

  if (data.stale) {
    notice(
      flow,
      'Atenção: as peças ou as chapas mudaram depois deste cálculo. ' +
        'Este plano é o do cálculo anterior — recalcule para ter o plano atualizado.',
      'warning',
    );
  }

  const unfitQty = result.unfit.reduce((a, u) => a + u.qty, 0);
  if (unfitQty > 0) {
    notice(
      flow,
      `${formatCount(unfitQty)} peça(s) não couberam nas chapas disponíveis. ` +
        'A lista está logo abaixo, em "Peças que não couberam".',
      'danger',
    );
  }

  const physical = stats.numSheets;
  kpis(flow, [
    [physical === 1 ? 'chapa usada' : 'chapas usadas', formatCount(physical)],
    ['de aproveitamento', formatPercent(stats.usedAreaRatio)],
    ['cortes', formatCount(stats.numCuts)],
    ['de corte no total', formatBigLength(stats.totalCutLength, settings.unit)],
  ]);

  sectionTitle(flow, 'Resumo');
  keyValues(flow, [
    ['Área total das chapas', formatArea(stats.totalSheetArea, settings.unit)],
    ['Área aproveitada', formatArea(stats.totalUsedArea, settings.unit)],
    ['Área desperdiçada', formatArea(stats.totalWasteArea, settings.unit)],
    ['Desperdício', formatPercent(stats.wasteRatio)],
    ['Peças posicionadas', formatCount(stats.numPlacedParts)],
    ['Peças não posicionadas', formatCount(stats.numUnfitParts)],
  ]);

  sectionTitle(flow, 'Configurações usadas');
  keyValues(flow, [
    ['Espessura de corte (serra)', formatKerf(settings.kerf)],
    ['Prioridade', priorityLabel(settings.priority)],
    ['Considerar veio', yesNo(settings.considerGrain)],
    ['Considerar material', yesNo(settings.considerMaterial)],
    ['Sobra mínima aproveitável', formatLength(settings.minTrimDimension, settings.unit, settings.decimalPlaces)],
  ]);

  sectionTitle(flow, 'Chapas do plano');
  drawTable(
    flow,
    [
      { header: '#', weight: 0.5, align: 'right' },
      { header: 'Chapa', weight: 2.2 },
      { header: 'Dimensões', weight: 2 },
      { header: 'Material', weight: 1.6 },
      { header: 'Qtd', weight: 0.7, align: 'right' },
      { header: 'Peças', weight: 0.8, align: 'right' },
      { header: 'Cortes', weight: 0.8, align: 'right' },
      { header: 'Aproveit.', weight: 1, align: 'right' },
    ],
    result.sheets.map((s) => [
      String(s.index + 1),
      s.label.trim() !== '' ? s.label.trim() : '—',
      dims(s.length, s.width, settings),
      s.material.trim() !== '' ? s.material.trim() : '—',
      formatCount(Math.max(1, s.repeat)),
      formatCount(s.placements.length),
      formatCount(s.numCuts),
      formatPercent(s.usedAreaRatio),
    ]),
    'Nenhuma chapa foi usada.',
  );

  if (result.unfit.length > 0) {
    sectionTitle(flow, 'Peças que não couberam', 'danger');
    drawTable(
      flow,
      [
        { header: 'Nome', weight: 1.7, wrap: true },
        { header: 'Dimensões', weight: 1.6 },
        { header: 'Qtd', weight: 0.5, align: 'right' },
        { header: 'Material', weight: 1.5, wrap: true },
        { header: 'Motivo', weight: 3.4, wrap: true },
      ],
      result.unfit.map((u) => [
        u.label.trim() !== '' ? u.label.trim() : '—',
        dims(u.length, u.width, settings),
        formatCount(u.qty),
        u.material.trim() !== '' ? u.material.trim() : '—',
        u.message,
      ]),
      '',
    );
  }

  const bandTotals = Object.entries(stats.edgeBandingTotals ?? {});
  if (bandTotals.length > 0) {
    sectionTitle(flow, 'Fita de borda');
    drawTable(
      flow,
      [
        { header: 'Fita', weight: 3 },
        { header: 'Comprimento total', weight: 2, align: 'right' },
      ],
      bandTotals.map(([id, total]) => {
        const band = data.edgeBands.find((b) => b.id === id);
        return [band ? band.name : id, formatBigLength(total, settings.unit)];
      }),
      '',
    );
  }

  if (result.warnings.length > 0) {
    sectionTitle(flow, 'Avisos');
    for (const w of result.warnings) {
      for (const [i, line] of wrapText(w, flow.width - 12, 'sans', 9).entries()) {
        flow.ensure(12);
        if (i === 0) flow.page.text('•', flow.left + 2, flow.y + 9, { size: 9, color: C.warning });
        flow.page.text(line, flow.left + 12, flow.y + 9, { size: 9, color: C.text });
        flow.gap(12);
      }
    }
    flow.gap(6);
  }
}

// -----------------------------------------------------------------------------
// UMA CHAPA
// -----------------------------------------------------------------------------

/** Altura reservada acima do desenho: titulo, subtitulo e numeros da chapa. */
const SHEET_HEAD_H = 52;

/**
 * Orientacao que deixa o desenho maior. A altura util desconta o cabecalho da
 * chapa, que e fixo.
 */
function bestOrientation(aspect: number): Orientation {
  const fit = (size: { width: number; height: number }): number => {
    const w = size.width - MARGIN * 2;
    const h = size.height - MARGIN * 2 - FOOTER_H - SHEET_HEAD_H;
    return Math.min(w / aspect, h);
  };
  return fit(A4_LANDSCAPE) > fit(A4_PORTRAIT) * 1.08 ? 'landscape' : 'portrait';
}

function buildSheet(flow: Flow, sheet: SheetLayout, total: number, data: PdfReportData): void {
  const { settings } = data;
  const svg = data.renderDiagram ? data.renderDiagram(sheet) : null;
  const orientation = svg ? bestOrientation(svgAspect(svg)) : 'portrait';
  flow.newPage(orientation);
  const page = flow.page;

  // --- cabecalho --------------------------------------------------------------
  const name = sheet.label.trim();
  const title = `Chapa ${sheet.index + 1} de ${total}` + (name !== '' ? ` — ${name}` : '');
  page.text(fitTextWidth(title, flow.width * 0.7, 'sansBold', 14), flow.left, flow.y + 13, {
    font: 'sansBold',
    size: 14,
    color: C.text,
  });
  page.text(`${formatPercent(sheet.usedAreaRatio)} de aproveitamento`, flow.left + flow.width, flow.y + 13, {
    font: 'sansBold',
    size: 10,
    align: 'right',
    color: C.accent,
  });

  const sub: string[] = [dims(sheet.length, sheet.width, settings)];
  if (sheet.material.trim() !== '') sub.push(sheet.material.trim());
  if (sheet.repeat > 1) sub.push(`cortar ${formatCount(sheet.repeat)} chapas iguais a esta`);
  page.text(fitTextWidth(sub.join('  ·  '), flow.width, 'sans', 9.5), flow.left, flow.y + 28, {
    size: 9.5,
    color: C.text,
  });
  page.text(
    [
      `${formatCount(sheet.placements.length)} peças`,
      `${formatCount(sheet.numCuts)} cortes`,
      `${formatBigLength(sheet.cutLength, settings.unit)} de corte`,
    ].join('  ·  '),
    flow.left,
    flow.y + 41,
    { size: 8.5, color: C.muted },
  );
  flow.gap(SHEET_HEAD_H);

  // --- desenho ----------------------------------------------------------------
  if (svg) {
    const drawn = drawSvg(page, svg, {
      x: flow.left,
      y: flow.y,
      width: flow.width,
      height: flow.remaining,
    });
    if (drawn) flow.gap(drawn.height + 14);
  }

  // --- tabelas ----------------------------------------------------------------
  sectionTitle(flow, `Peças da chapa ${sheet.index + 1}`);
  drawTable(
    flow,
    [
      { header: '#', weight: 0.5, align: 'right' },
      { header: 'Nome', weight: 2.6 },
      { header: 'Dimensões', weight: 2 },
      { header: 'Qtd', weight: 0.6, align: 'right' },
      { header: 'Material', weight: 1.6 },
      { header: 'Girada', weight: 0.8 },
    ],
    groupPlacements(sheet, settings),
    'Nenhuma peça foi posicionada nesta chapa.',
  );

  sectionTitle(flow, `Sequência de cortes da chapa ${sheet.index + 1}`);
  drawTable(
    flow,
    [
      { header: '#', weight: 0.45, align: 'right' },
      { header: 'Peça-mãe', weight: 1.7 },
      { header: 'Corte', weight: 2 },
      { header: 'Compr.', weight: 1 },
      { header: 'Resultado', weight: 3.2 },
    ],
    sheet.cuts.map((c) => [
      String(c.seq),
      dims(c.parent.length, c.parent.width, settings),
      `${c.axis === 'x' ? 'Vertical' : 'Horizontal'} ${c.axis} = ${formatLength(
        c.pos,
        settings.unit,
        settings.decimalPlaces,
      )}`,
      formatLength(c.length, settings.unit, settings.decimalPlaces),
      `${childText(c.childA, settings)}  \\  ${childText(c.childB, settings)}`,
    ]),
    'Nenhum corte necessário nesta chapa.',
  );

  sectionTitle(flow, `Sobras da chapa ${sheet.index + 1}`);
  drawTable(
    flow,
    [
      { header: '#', weight: 0.5, align: 'right' },
      { header: 'Dimensões', weight: 2.2 },
      { header: 'Posição (x · y)', weight: 2 },
      { header: 'Área', weight: 1.2, align: 'right' },
      { header: 'Aproveitável', weight: 1.1 },
    ],
    sheet.offcuts.map((o, i) => [
      String(i + 1),
      dims(o.length, o.width, settings),
      `${formatLength(o.x, settings.unit, settings.decimalPlaces)} · ${formatLength(
        o.y,
        settings.unit,
        settings.decimalPlaces,
      )}`,
      formatArea(o.area, settings.unit),
      yesNo(o.usable),
    ]),
    'Esta chapa não deixou sobras.',
  );
}

function childText(
  child: { length: number; width: number; isWaste: boolean },
  settings: Settings,
): string {
  const text = dims(child.length, child.width, settings);
  return child.isWaste ? `${text} (sobra)` : text;
}

/** Pecas iguais da chapa numa linha so, com a quantidade. */
function groupPlacements(sheet: SheetLayout, settings: Settings): string[][] {
  const groups = new Map<string, { label: string; length: number; width: number; material: string; rotated: boolean; qty: number }>();
  for (const pl of sheet.placements) {
    const key = `${pl.label}|${pl.length}|${pl.width}|${pl.material}|${pl.rotated ? 1 : 0}`;
    const hit = groups.get(key);
    if (hit) {
      hit.qty++;
      continue;
    }
    groups.set(key, {
      label: pl.label,
      length: pl.length,
      width: pl.width,
      material: pl.material,
      rotated: pl.rotated,
      qty: 1,
    });
  }
  return [...groups.values()].map((g, i) => [
    String(i + 1),
    g.label.trim() !== '' ? g.label.trim() : '—',
    dims(g.length, g.width, settings),
    formatCount(g.qty),
    g.material.trim() !== '' ? g.material.trim() : '—',
    yesNo(g.rotated),
  ]);
}

// -----------------------------------------------------------------------------
// RODAPE
// -----------------------------------------------------------------------------

function drawFooters(doc: PdfDocument, projectName: string): void {
  const pages = doc.allPages;
  const label = projectName.trim() !== '' ? projectName.trim() : 'Plano de corte';
  pages.forEach((page, i) => {
    const y = page.height - MARGIN + 6;
    page
      .strokeColor(C.line)
      .lineWidth(0.5)
      .dash([])
      .moveTo(MARGIN, y - 10)
      .lineTo(page.width - MARGIN, y - 10)
      .stroke();
    page.text(fitTextWidth(`Pedra · ${label}`, page.width * 0.6, 'sans', 7.5), MARGIN, y, {
      size: 7.5,
      color: C.muted,
    });
    page.text(`Página ${i + 1} de ${pages.length}`, page.width - MARGIN, y, {
      size: 7.5,
      align: 'right',
      color: C.muted,
    });
  });
}

// -----------------------------------------------------------------------------
// MONTAGEM
// -----------------------------------------------------------------------------

/** Monta o documento inteiro. Nao toca em download — util para teste. */
export function buildPdfReport(data: PdfReportData): PdfDocument {
  const now = data.now ?? new Date();
  const doc = new PdfDocument({
    title: `${data.projectName.trim() || 'Projeto'} — plano de corte`,
    subject: 'Plano de corte',
  });

  const flow = new Flow(doc, 'portrait');
  buildCover(flow, data, now);

  const sheets = data.result.sheets;
  for (const sheet of sheets) buildSheet(flow, sheet, sheets.length, data);

  drawFooters(doc, data.projectName);
  return doc;
}

/** Monta o PDF e dispara o download. Devolve o nome do arquivo gerado. */
export function downloadPdfReport(data: PdfReportData): string {
  const now = data.now ?? new Date();
  const doc = buildPdfReport({ ...data, now });
  const filename = pdfFilename(data.projectName, now);
  downloadBlob(filename, doc.toBlob(now));
  return filename;
}
